import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, memo } from "react";
import Analytics from './components/Analytics';
import { CacheProvider } from './contexts/CacheContext';
import ErrorBoundary from './components/ErrorBoundary';

// OTIMIZAÇÃO: Lazy loading - Carregar componentes sob demanda
const LandingPage = lazy(() => import("@/components/LandingPage"));
const StoreFront = lazy(() => import("@/components/StoreFront"));
const AdminDashboard = lazy(() => import("@/components/AdminDashboard"));
const ProductsDashboard = lazy(() => import("@/components/ProductsDashboard"));
const UploadPage = lazy(() => import("@/components/UploadPage"));
const VideoPlayer = lazy(() => import("@/components/VideoPlayer"));
const VideosListPage = lazy(() => import("@/components/VideosListPage"));
const VideoAdmin = lazy(() => import("@/components/VideoAdmin"));
const StoreConfig = lazy(() => import("@/components/StoreConfig"));
const WhatsAppDebug = lazy(() => import("@/components/WhatsAppDebug"));
const NewCampaign = lazy(() => import("@/components/NewCampaign"));
const CampaignCover = lazy(() => import("@/components/CampaignCover"));
// FloatingWhatsApp removido a pedido do usuário
const FacebookPixel = lazy(() => import("@/components/FacebookPixel"));
const DevicePreview = lazy(() => import("@/components/DevicePreview"));
const VirtualTryOnStandalone = lazy(() => import("@/components/VirtualTryOnStandalone"));
const ModelSwap = lazy(() => import("@/components/ModelSwap"));
const ModelCreate = lazy(() => import("@/components/ModelCreate"));
const ProductToModel = lazy(() => import("@/components/ProductToModel"));
const LandingPageView = lazy(() => import("@/components/LandingPageView"));
const ABTestingDashboard = lazy(() => import("@/components/ABTestingDashboard"));
const PerformanceDashboard = lazy(() => import("@/components/PerformanceDashboard"));
const AIContentGenerator = lazy(() => import("@/components/AIContentGenerator"));
const MultiChannelPosting = lazy(() => import("@/components/MultiChannelPosting"));
const AuthCallback = lazy(() => import("@/components/AuthCallback"));
const CreativeStudioIA = lazy(() => import("@/components/CreativeStudioIA"));
const OverviewDashboard = lazy(() => import("@/components/OverviewDashboard"));
const InstagramSetup = lazy(() => import("@/components/InstagramSetup"));
const InstagramTokenFixer = lazy(() => import("@/components/InstagramTokenFixer"));
const InstagramStories = lazy(() => import("@/components/InstagramStories"));
const CreatePost = lazy(() => import("@/components/CreatePost"));
const ScheduledPosts = lazy(() => import("@/components/ScheduledPosts"));
const AISegmentationWizard = lazy(() => import("@/components/AISegmentationWizard"));
const MetaPermanentTokenGenerator = lazy(() => import("@/components/MetaPermanentTokenGenerator"));
const IntelligentCampaignHub = lazy(() => import("@/components/IntelligentCampaignHub"));
const MetaCampaignStudio = lazy(() => import("@/components/MetaCampaignStudio"));
const CampaignPublisher = lazy(() => import("@/components/CampaignPublisher"));
const StoryCreator = lazy(() => import("@/components/StoryCreator"));
const DigistatDashboard = lazy(() => import("@/components/DigistatDashboard"));
const DigistatMobile = lazy(() => import("@/components/DigistatMobile"));
const ConferenciaNFe = lazy(() => import("@/components/ConferenciaNFe"));
const ConferenciaInventario = lazy(() => import("@/components/ConferenciaInventario"));
const ShopeeIntegration = lazy(() => import("@/components/ShopeeIntegration"));
const ShopeePricing = lazy(() => import("@/components/ShopeePricing"));
const ShopeePublish = lazy(() => import("@/components/ShopeePublish"));
const ShopeeCadastro = lazy(() => import("@/components/ShopeeCadastro"));
const VitrinePage = lazy(() => import("@/components/VitrinePage"));
const CupomDigitalPage = lazy(() => import("@/components/CupomDigitalPage"));

// Componente que detecta mobile e renderiza o componente apropriado
const DigistatResponsive = () => {
  const isMobile = window.innerWidth <= 768;
  return isMobile ? <DigistatMobile /> : <DigistatDashboard />;
};

// Componente que redireciona mobile para Digisat
const MobileRedirect = ({ children }) => {
  const isMobile = window.innerWidth <= 768;
  if (isMobile) {
    // No mobile, redireciona para Digisat
    window.location.href = '/digisat';
    return null;
  }
  return children;
};

// Importar Login e ProtectedRoute diretamente (não lazy) para evitar problemas
import Login from "./components/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicVirtualTryOn from "./components/PublicVirtualTryOn";

// Componente de Loading otimizado - mais leve
const LoadingScreen = memo(() => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600 mb-3"></div>
      <p className="text-purple-600 font-medium">Carregando...</p>
    </div>
  </div>
));

function App() {
  return (
    <ErrorBoundary>
      <CacheProvider>
        <div className="App min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 overflow-x-hidden">
          <BrowserRouter>
            {/* Analytics - Tracking automático */}
            <Analytics />
          
          <Suspense fallback={<LoadingScreen />}>
            {/* Facebook Pixel - Rastreia todas as páginas */}
            <FacebookPixel />
          
          <Routes>
          {/* 🔐 LOGIN */}
          <Route path="/login" element={<Login />} />
          
          {/* 🎯 LANDING PAGE DE VENDAS */}
          <Route path="/vendas" element={<LandingPage />} />
          
          {/* 🌐 ÁREA PÚBLICA - Cliente */}
          <Route path="/loja" element={<StoreFront />} />
          <Route path="/landing/:campaignId" element={<LandingPageView />} />
          <Route path="/watch/:videoId" element={<VideoPlayer />} />
          <Route path="/campaign/:videoId" element={<CampaignCover />} />
          
          {/* Ver em Mim Público */}
          <Route path="/ver-em-mim/:productId" element={<PublicVirtualTryOn />} />
          
          {/* Cupom Digital Condicional - Público */}
          <Route path="/condicional/:codigo" element={<CupomDigitalPage />} />
          
          {/* 🔐 ÁREA ADMINISTRATIVA - PROTEGIDA */}
          <Route path="/admin" element={<ProtectedRoute><MobileRedirect><AdminDashboard /></MobileRedirect></ProtectedRoute>} />
          <Route path="/admin/products" element={<ProtectedRoute><ProductsDashboard /></ProtectedRoute>} />
          <Route path="/admin/campaigns" element={<ProtectedRoute><VideosListPage /></ProtectedRoute>} />
          <Route path="/admin/:videoId" element={<ProtectedRoute><VideoAdmin /></ProtectedRoute>} />
          <Route path="/campaign/new" element={<ProtectedRoute><NewCampaign /></ProtectedRoute>} />
          <Route path="/config" element={<ProtectedRoute><StoreConfig /></ProtectedRoute>} />
          <Route path="/virtual-tryon" element={<ProtectedRoute><VirtualTryOnStandalone /></ProtectedRoute>} />
          <Route path="/model-swap" element={<ProtectedRoute><ModelSwap /></ProtectedRoute>} />
          <Route path="/model-create" element={<ProtectedRoute><ModelCreate /></ProtectedRoute>} />
          <Route path="/product-to-model" element={<ProtectedRoute><ProductToModel /></ProtectedRoute>} />
          <Route path="/ab-testing" element={<ABTestingDashboard />} />
          <Route path="/performance" element={<PerformanceDashboard />} />
          <Route path="/ai-content" element={<AIContentGenerator />} />
          <Route path="/multi-channel" element={<MultiChannelPosting />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/creative-studio" element={<CreativeStudioIA />} />
          <Route path="/overview" element={<OverviewDashboard />} />
          <Route path="/instagram-setup" element={<InstagramSetup />} />
          <Route path="/token-fixer" element={<InstagramTokenFixer />} />
          <Route path="/instagram-stories" element={<InstagramStories />} />
          <Route path="/story-creator" element={<ProtectedRoute><StoryCreator /></ProtectedRoute>} />
          
          {/* 🔄 INTEGRAÇÃO DIGISAT */}
          <Route path="/digisat" element={<ProtectedRoute><DigistatResponsive /></ProtectedRoute>} />
          <Route path="/admin/digisat" element={<ProtectedRoute><DigistatResponsive /></ProtectedRoute>} />
          <Route path="/admin/conferencia-nfe" element={<ProtectedRoute><ConferenciaNFe /></ProtectedRoute>} />
          <Route path="/admin/conferencia-inventario" element={<ProtectedRoute><ConferenciaInventario /></ProtectedRoute>} />
          
          {/* 🛒 SHOPEE MARKETPLACE */}
          <Route path="/admin/shopee" element={<ProtectedRoute><ShopeeIntegration /></ProtectedRoute>} />
          <Route path="/admin/shopee/pricing" element={<ProtectedRoute><ShopeePricing /></ProtectedRoute>} />
          <Route path="/admin/shopee/publish" element={<ProtectedRoute><ShopeePublish /></ProtectedRoute>} />
          <Route path="/admin/shopee/cadastro" element={<ProtectedRoute><ShopeeCadastro /></ProtectedRoute>} />
          
          {/* 🎁 VITRINE COMPARTILHÁVEL - Página pública com produtos */}
          <Route path="/vitrine/:vitrineId" element={<VitrinePage />} />
          
          {/* 📤 META PUBLISHING - Publicar no Instagram/Facebook */}
          <Route path="/admin/create-post" element={<ProtectedRoute><CreatePost /></ProtectedRoute>} />
          <Route path="/admin/scheduled-posts" element={<ProtectedRoute><ScheduledPosts /></ProtectedRoute>} />
          <Route path="/admin/meta-permanent-token" element={<ProtectedRoute><MetaPermanentTokenGenerator /></ProtectedRoute>} />

          <Route path="/admin/ai-segmentation-wizard" element={<ProtectedRoute><AISegmentationWizard /></ProtectedRoute>} />


          {/* 🚀 META CAMPAIGN STUDIO - HUB UNIFICADO */}

          {/* 📤 PUBLICAR CAMPANHA - Wizard Completo */}
          <Route path="/admin/publish-campaign" element={<ProtectedRoute><CampaignPublisher /></ProtectedRoute>} />

          <Route path="/admin/campaign-studio" element={<ProtectedRoute><MetaCampaignStudio /></ProtectedRoute>} />

          
          {/* 🛠️ FERRAMENTAS E UTILITÁRIOS - PROTEGIDAS */}
          <Route path="/" element={<ProtectedRoute><MobileRedirect><UploadPage /></MobileRedirect></ProtectedRoute>} />
          <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
          <Route path="/videos" element={<ProtectedRoute><VideosListPage /></ProtectedRoute>} />
          <Route path="/debug-whatsapp" element={<ProtectedRoute><WhatsAppDebug /></ProtectedRoute>} />
          <Route path="/preview" element={<ProtectedRoute><DevicePreview /></ProtectedRoute>} />
          
          {/* URL amigável para campanhas - DEVE ser a última rota (fallback) */}
          <Route path="/:slug" element={<VideoPlayer />} />
          </Routes>
          
        </Suspense>
      </BrowserRouter>
      </div>
    </CacheProvider>
    </ErrorBoundary>
  );
}

export default App;
